import { c as _c } from "react/compiler-runtime";
import { useQueryClient } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { createMemoryHistory, createRouter, RouterProvider } from '@tanstack/react-router';
import { useState } from 'react';
import { InitShimmer } from './components/init-shimmer';
import { useIdvToIdv2Sync } from './features/idv-to-idv2';
import { routeTree } from './routes/routeTree.gen';
import { shouldWaitForComponentsSdk } from './utils/route-helpers';
import { setupRouteTracking } from './utils/route-tracking';

// AppShell renders the real navigation header in the frame this covers, so the skeleton must not
// reserve header space of its own.
const PendingShimmer = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <InitShimmer reserveHeader={false} />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
export type Idv2Props = {
  /** Initial route when embedded: sandbox-outcome (pre-identify), identify flow, onboarding flow (with token in path), auth-methods management, or handoff (passkey registration target). */
  initialPath?: '/sandbox-outcome' | '/identify' | '/onboarding' | '/auth-methods' | '/handoff';
  /** Identify session scope: 'auth' (includes validate + passkey offer after identify) or 'onboarding' (direct to onboarding after identify). */
  scope?: 'auth' | 'onboarding';
  /** When set, identify completion calls this then navigates to /onboarding (unified flow). */
  onIdentifyDone?: (authToken: string) => void;
  /** Called when auth-methods management flow completes. */
  onAuthMethodsDone?: () => void;
};
export const Idv2 = t0 => {
  const $ = _c(18);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    initialPath: t2,
    scope: t3,
    onIdentifyDone,
    onAuthMethodsDone
  } = t1;
  const initialPath = t2 === undefined ? "/onboarding" : t2;
  const scope = t3 === undefined ? "onboarding" : t3;
  const initialValues = useIdvToIdv2Sync();
  const queryClient = useQueryClient();
  let t4;
  if ($[2] !== initialPath || $[3] !== initialValues.componentsSdkRelay || $[4] !== initialValues.onboardingSessionToken) {
    t4 = () => {
      const token = initialValues.onboardingSessionToken;
      let path;
      if (initialPath === "/onboarding" && shouldWaitForComponentsSdk(initialValues.componentsSdkRelay)) {
        path = `/wait-for-components-sdk/${token}`;
      } else {
        if (initialPath === "/onboarding") {
          path = `/onboarding/${token}`;
        } else {
          path = initialPath;
        }
      }
      const history = createMemoryHistory({
        initialEntries: [path]
      });
      const r = createRouter({
        routeTree,
        history,
        defaultPreload: "intent",
        defaultPreloadDelay: 100
      });
      setupRouteTracking(r);
      return r;
    };
    $[2] = initialPath;
    $[3] = initialValues.componentsSdkRelay;
    $[4] = initialValues.onboardingSessionToken;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const [router] = useState(t4);
  let t5;
  if ($[6] !== initialValues || $[7] !== onAuthMethodsDone || $[8] !== onIdentifyDone || $[9] !== queryClient || $[10] !== scope) {
    t5 = {
      ...initialValues,
      queryClient,
      scope,
      onIdentifyDone,
      onAuthMethodsDone
    };
    $[6] = initialValues;
    $[7] = onAuthMethodsDone;
    $[8] = onIdentifyDone;
    $[9] = queryClient;
    $[10] = scope;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== router || $[13] !== t5) {
    t6 = <RouterProvider defaultPendingMs={0} defaultPendingComponent={PendingShimmer} router={router} context={t5} />;
    $[12] = router;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = process.env.NODE_ENV === "development" && <ReactQueryDevtools />;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== t6) {
    t8 = <>{t6}{t7}</>;
    $[16] = t6;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  return t8;
};